import React, { useCallback } from "react";
import { Upload, Image, Button, message, Space, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";

const PhotoPopover = ({ 
  pictures, 
  entity, 
  entityId, 
  token, 
  onRefresh,
  onClose, // Зарезервировано для будущего использования
  messageApi: externalMessageApi,
  fallbackEntity,
  fallbackEntityId
}) => {
  const [internalMessageApi, contextHolder] = message.useMessage();
  const messageApi = externalMessageApi || internalMessageApi;


  // Универсальный обработчик загрузки фотографий
  // Если есть фото - заменяет первое фото
  // Если нет фото - добавляет новое фото
  const handleUpload = useCallback(
    async (file) => {
      try {
        // Если есть фото - заменяем первое фото
        if (pictures.length > 0) {
          // Заменяем первое фото: сначала удаляем старое, потом загружаем новое
          try {
            await apiRequests.pictures.deleteOrder(pictures[0].id, token);
          } catch (deleteError) {
            console.error("Ошибка удаления старого фото:", deleteError);
            // Продолжаем загрузку нового фото даже если удаление не удалось
          }
        }

        // Загружаем новое фото
        const formData = new FormData();
        // В customRequest file может быть обёрнутым объектом, используем originFileObj если есть
        const fileToUpload = file.originFileObj || file;
        formData.append("file", fileToUpload);

        const response = await apiRequests.pictures.postPictureOrder(
          entity,
          entityId,
          token,
          formData
        );

        // Если есть fallback (например, для цен - номенклатура), сохраняем фото и туда
        if (fallbackEntity && fallbackEntityId) {
          try {
            const fallbackFormData = new FormData();
            fallbackFormData.append("file", fileToUpload);
            await apiRequests.pictures.postPictureOrder(
              fallbackEntity,
              fallbackEntityId,
              token,
              fallbackFormData
            );
          } catch (fallbackError) {
            // Игнорируем ошибки при сохранении в fallback, главное что в основную сущность сохранилось
            console.error("Ошибка сохранения фото в fallback:", fallbackError);
          }
        }

        const successMessage = 
          pictures.length > 0
            ? (response?.data?.message || "Фото успешно изменено")
            : (response?.data?.message || "Фото успешно добавлено");
        if (externalMessageApi) {
          messageApi.open({ type: 'success', content: successMessage, duration: 3 });
        } else {
          messageApi.success(successMessage);
        }
        onRefresh();
        return response;
      } catch (error) {
        console.error("Ошибка загрузки:", error);
        let errorMessage = "Ошибка при загрузке фото";
        
        // Извлекаем сообщение об ошибке, обрабатывая разные форматы
        if (error?.response?.data?.message) {
          errorMessage = typeof error.response.data.message === 'string' 
            ? error.response.data.message 
            : JSON.stringify(error.response.data.message);
        } else if (error?.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => 
              typeof err === 'string' ? err : err?.msg || JSON.stringify(err)
            ).join(', ');
          } else if (error.response.data.detail && typeof error.response.data.detail === 'object') {
            // Если detail - это объект, пытаемся извлечь сообщение
            errorMessage = error.response.data.detail.message || 
                          error.response.data.detail.msg || 
                          JSON.stringify(error.response.data.detail);
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        } else if (error?.response?.data) {
          // Если есть data, но нет message или detail
          errorMessage = typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data);
        } else if (error?.message) {
          errorMessage = typeof error.message === 'string' 
            ? error.message 
            : JSON.stringify(error.message);
        }
        
        if (externalMessageApi) {
          messageApi.open({ type: 'error', content: errorMessage, duration: 4 });
        } else {
          messageApi.error(errorMessage);
        }
        throw error;
      }
    },
    [entity, entityId, token, onRefresh, messageApi, externalMessageApi, pictures, fallbackEntity, fallbackEntityId]
  );


  const handleRemove = useCallback(
    async (pictureId) => {
      try {
        const response = await apiRequests.pictures.deleteOrder(pictureId, token);
        
        // Если есть fallback, удаляем фото и оттуда (ищем по URL)
        if (fallbackEntity && fallbackEntityId && pictures.length > 0) {
          const pictureToDelete = pictures.find(p => p.id === pictureId);
          if (pictureToDelete) {
            try {
              // Пробуем найти и удалить такое же фото из fallback по URL
              const fallbackResponse = await apiRequests.pictures.getItemsOrder(
                fallbackEntity,
                fallbackEntityId,
                token
              );
              
              let fallbackPicturesData = [];
              if (Array.isArray(fallbackResponse.data)) {
                fallbackPicturesData = fallbackResponse.data;
              } else if (fallbackResponse.data?.result && Array.isArray(fallbackResponse.data.result)) {
                fallbackPicturesData = fallbackResponse.data.result;
              } else if (fallbackResponse.data?.data && Array.isArray(fallbackResponse.data.data)) {
                fallbackPicturesData = fallbackResponse.data.data;
              }
              
              // Ищем фото с таким же URL в fallback
              const matchingFallbackPicture = fallbackPicturesData.find(
                pic => pic.url === pictureToDelete.url || 
                       pic.url === pictureToDelete.name ||
                       (pic.url && pictureToDelete.url && pic.url.includes(pictureToDelete.url.split('/').pop()))
              );
              
              if (matchingFallbackPicture?.id) {
                await apiRequests.pictures.deleteOrder(matchingFallbackPicture.id, token);
              }
            } catch (fallbackError) {
              // Игнорируем ошибки при удалении из fallback
              console.error("Ошибка удаления фото из fallback:", fallbackError);
            }
          }
        }
        
        const successMessage = 
          response?.data?.message || 
          "Фото удалено";
        if (externalMessageApi) {
          messageApi.open({ type: 'success', content: successMessage, duration: 3 });
        } else {
          messageApi.success(successMessage);
        }
        onRefresh();
      } catch (error) {
        console.error("Ошибка удаления:", error);
        let errorMessage = "Ошибка при удалении фото";
        
        // Извлекаем сообщение об ошибке, обрабатывая разные форматы
        if (error?.response?.data?.message) {
          errorMessage = typeof error.response.data.message === 'string' 
            ? error.response.data.message 
            : JSON.stringify(error.response.data.message);
        } else if (error?.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => 
              typeof err === 'string' ? err : err?.msg || JSON.stringify(err)
            ).join(', ');
          } else if (error.response.data.detail && typeof error.response.data.detail === 'object') {
            // Если detail - это объект, пытаемся извлечь сообщение
            errorMessage = error.response.data.detail.message || 
                          error.response.data.detail.msg || 
                          JSON.stringify(error.response.data.detail);
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        } else if (error?.response?.data) {
          // Если есть data, но нет message или detail
          errorMessage = typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data);
        } else if (error?.message) {
          errorMessage = typeof error.message === 'string' 
            ? error.message 
            : JSON.stringify(error.message);
        }
        
        if (externalMessageApi) {
          messageApi.open({ type: 'error', content: errorMessage, duration: 4 });
        } else {
          messageApi.error(errorMessage);
        }
      }
    },
    [token, onRefresh, messageApi, externalMessageApi, fallbackEntity, fallbackEntityId, pictures]
  );

  return (
    <div style={{ width: "240px", padding: "12px" }}>
      {!externalMessageApi && contextHolder}

      {/* Сетка мини-превью */}
      {pictures.length > 0 && (
        <Image.PreviewGroup
          items={pictures.map((pic) => ({
            src: pic.url,
            alt: pic.name,
          }))}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
              marginBottom: "12px",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {pictures.map((pic, index) => (
              <div
                key={pic.id}
                style={{
                  position: "relative",
                  aspectRatio: "1",
                  borderRadius: "4px",
                  overflow: "hidden",
                  border: "1px solid #f0f0f0",
                  cursor: "pointer",
                }}
              >
                <Image
                  src={pic.url}
                  alt={pic.name}
                  width="100%"
                  height="100%"
                  style={{
                    objectFit: "cover",
                  }}
                  preview={{
                    mask: false,
                    toolbarRender: () => null,
                  }}
                />
                <Popconfirm
                  title="Удалить фото?"
                  description="Это действие нельзя отменить"
                  onConfirm={() => handleRemove(pic.id)}
                  okText="Да"
                  cancelText="Нет"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      backgroundColor: "rgba(255,255,255,0.95)",
                      minWidth: "24px",
                      height: "24px",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )}

      {/* Кнопки действий */}
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        {token && entity && entityId ? (
          <Upload
            customRequest={({ file, onSuccess, onError }) => {
              handleUpload(file)
                .then((response) => {
                  onSuccess?.(response);
                })
                .catch((error) => {
                  onError?.(error);
                });
            }}
            showUploadList={false}
            accept="image/*"
            multiple={false}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              style={{ fontSize: "13px" }}
            >
              Добавить фото
            </Button>
          </Upload>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            disabled
            style={{ fontSize: "13px" }}
          >
            Добавить фото (недостаточно данных)
          </Button>
        )}

      </Space>
    </div>
  );
};

export default PhotoPopover;

